// /profile - Cofounder public profile editor.
// Feature 12.2. Edit display name, bio, photo URL, specialties,
// location, slug, landing page toggle.

import { useEffect, useState } from 'react';
import { PortalLayout } from '../components/PortalLayout';
import { Card, CardLabel, Button, Input, Textarea, colors, headingStyle, pageWrapperStyle, mutedStyle, errorBannerStyle, footerStyle, linkStyle, useToast } from '../components/ui';
import { useLang, fmt } from '../i18n';

interface ProfileData {
  display_name: string;
  headline: string;
  bio: string;
  photo_url: string;
  vertical_focus: string[];
  city: string;
  territory_country: string;
  public_slug: string;
  public_landing_enabled: boolean;
  contact_email: string;
  contact_phone: string;
  linkedin_url: string;
}

export default function Profile() {
  const { t } = useLang();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/partner/profile');
        if (!res.ok) {
          if (!cancelled) { setError(`HTTP ${res.status}`); setLoading(false); }
          return;
        }
        const data = (await res.json()) as ProfileData;
        if (!cancelled) { setProfile(data); setLoading(false); }
      } catch {
        if (!cancelled) { setError('network_error'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const res = await fetch('/api/partner/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      });
      if (res.ok) {
        toast.show('success', t.common.saved);
      } else {
        toast.show('error', t.common.error);
      }
    } catch {
      toast.show('error', t.common.networkError);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof ProfileData, value: string | boolean | string[]) => {
    if (!profile) return;
    setProfile({ ...profile, [field]: value });
  };

  return (
    <PortalLayout>
      <div style={{ ...pageWrapperStyle, maxWidth: '800px' }}>
        <h1 style={{ ...headingStyle, marginBottom: '8px' }}>
          {t.profile.title}
        </h1>
        <p style={{ ...mutedStyle, marginBottom: '24px' }}>
          {t.profile.subtitle}
        </p>

        {loading && <div style={mutedStyle}>{t.profile.loadingProfile}</div>}
        {error && (
          <div style={errorBannerStyle}>
            {t.common.error}: {error}
          </div>
        )}

        {profile && (
          <>
            <Card size="lg">
              <Input
                label={t.profile.fieldDisplayName}
                value={profile.display_name}
                onChange={(e) => updateField('display_name', e.target.value)}
                placeholder={t.profile.fieldDisplayNamePlaceholder}
              />

              <Input
                label={t.profile.fieldHeadline}
                value={profile.headline}
                onChange={(e) => updateField('headline', e.target.value)}
                placeholder={t.profile.fieldHeadlinePlaceholder}
              />

              <Textarea
                label={t.profile.fieldBio}
                value={profile.bio}
                onChange={(e) => updateField('bio', e.target.value)}
                placeholder={t.profile.fieldBioPlaceholder}
                style={{ minHeight: '120px' }}
              />

              <Input
                label={t.profile.fieldPhotoUrl}
                value={profile.photo_url}
                onChange={(e) => updateField('photo_url', e.target.value)}
                placeholder={t.profile.fieldPhotoUrlPlaceholder}
              />
            </Card>

            <Card size="lg">
              <Input
                label={t.profile.fieldCity}
                value={profile.city}
                onChange={(e) => updateField('city', e.target.value)}
                placeholder={t.profile.fieldCityPlaceholder}
              />

              <Input
                label={t.profile.fieldCountry}
                value={profile.territory_country}
                onChange={(e) => updateField('territory_country', e.target.value)}
                placeholder={t.profile.fieldCountryPlaceholder}
              />

              <Input
                label={t.profile.fieldSpecialties}
                value={(profile.vertical_focus ?? []).join(', ')}
                onChange={(e) => updateField('vertical_focus', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                placeholder={t.profile.fieldSpecialtiesPlaceholder}
              />
            </Card>

            <Card size="lg">
              <CardLabel>{t.profile.fieldSlug}</CardLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <span style={{ color: colors.mutedLight, fontSize: '0.85rem' }}>{t.profile.slugPrefix}</span>
                <Input
                  value={profile.public_slug}
                  onChange={(e) => updateField('public_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder={t.profile.slugPlaceholder}
                  inputStyle={{ marginBottom: 0, flex: 1 }}
                />
              </div>

              <Input
                label={t.profile.fieldContactEmail}
                value={profile.contact_email}
                onChange={(e) => updateField('contact_email', e.target.value)}
                placeholder={t.profile.fieldContactEmailPlaceholder}
              />

              <Input
                label={t.profile.fieldLinkedIn}
                value={profile.linkedin_url}
                onChange={(e) => updateField('linkedin_url', e.target.value)}
                placeholder={t.profile.fieldLinkedInPlaceholder}
              />

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  checked={profile.public_landing_enabled}
                  onChange={(e) => updateField('public_landing_enabled', e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{ fontSize: '0.9rem', color: colors.text }}>{t.profile.enableLanding}</div>
                  <div style={{ fontSize: '0.75rem', color: colors.mutedLight }}>
                    {fmt(t.profile.enableLandingDesc, { slug: profile.public_slug || 'your-slug' })}
                  </div>
                </div>
              </div>
            </Card>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button variant="secondary" busy={saving} onClick={handleSave}>
                {t.profile.saveProfile}
              </Button>
            </div>

            {profile.public_landing_enabled && profile.public_slug && (
              <Card style={{ marginTop: '24px', borderColor: colors.cyanBtnBorder }}>
                <CardLabel>{t.profile.linksSection}</CardLabel>
                <div style={{ fontSize: '0.85rem', color: colors.muted, lineHeight: 2 }}>
                  <div>
                    {t.profile.linkLandingPage}{' '}
                    <a
                      href={`https://www.ailysagency.ca/partners/${profile.public_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                    >
                      ailysagency.ca/partners/{profile.public_slug}
                    </a>
                  </div>
                  <div>
                    {t.profile.linkSigImage}{' '}
                    <a
                      href={`https://www.ailysagency.ca/api/cofounder-signature?slug=${profile.public_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={linkStyle}
                    >
                      {t.profile.copySigUrl}
                    </a>
                  </div>
                </div>
              </Card>
            )}

            <div style={footerStyle}>
              {t.common.poweredBy}
            </div>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
